/**
 * Cálculo del centro del mapa del paso 3 (TG-123).
 *
 * Aislado del componente para poder testear la precedencia sin montar Leaflet.
 * `latitude`/`longitude` son **sólo lectura**: centrar el mapa nunca escribe coordenadas
 * (eso lo hacen el click, el arrastre del pin o el tipeo del operador).
 */
import { COLOMBIA_DEPARTMENT_CENTERS, COLOMBIA_MUNICIPALITIES } from './colombiaMunicipalities';

/** Centro aproximado de Colombia (para el estado "sin zona ni pin"). */
export const COLOMBIA_CENTER: [number, number] = [4.5709, -74.2973];
const COLOMBIA_ZOOM = 5;
const PIN_ZOOM = 15;
const MUNICIPALITY_ZOOM = 13;
const DEPARTMENT_ZOOM = 9;

export interface MapZone {
  center: [number, number];
  zoom: number;
  /** Texto del contexto, p.ej. "Cundinamarca · Chía" (municipio) o "Cundinamarca". */
  label: string;
}

export interface MapTarget {
  center: [number, number];
  zoom: number;
  /** Zona elegida (municipio o departamento) ignorando el pin. `null` si no hay zona. */
  zone: MapZone | null;
}

/**
 * Centro de la zona elegida por el operador. El municipio manda sobre el departamento.
 *
 * ⚠️ El lookup es por `(state, city)`: los nombres de municipio **se repiten entre
 * departamentos** (p. ej. "La Unión"), así que nunca hay que buscar sólo por nombre.
 */
export function resolveZone(state?: string, city?: string): MapZone | null {
  if (!state) {
    return null;
  }
  const municipality = city
    ? COLOMBIA_MUNICIPALITIES[state]?.find((entry) => entry.name === city)
    : undefined;
  if (municipality && city) {
    return {
      center: [municipality.lat, municipality.lng],
      zoom: MUNICIPALITY_ZOOM,
      label: `${state} · ${city}`,
    };
  }
  const department = COLOMBIA_DEPARTMENT_CENTERS[state];
  if (department) {
    return { center: [department.lat, department.lng], zoom: DEPARTMENT_ZOOM, label: state };
  }
  return null;
}

/**
 * Centro del mapa con la precedencia acordada:
 * 1. pin existente (lat/lng con valor) → zoom de calle;
 * 2. municipio (`state`+`city` en el dataset) → zoom de municipio;
 * 3. departamento (`state` con valor) → zoom de departamento;
 * 4. nada → Colombia.
 */
export function resolveMapTarget(input: {
  latitude?: number | null;
  longitude?: number | null;
  state?: string;
  city?: string;
}): MapTarget {
  const zone = resolveZone(input.state, input.city);
  if (typeof input.latitude === 'number' && typeof input.longitude === 'number') {
    return { center: [input.latitude, input.longitude], zoom: PIN_ZOOM, zone };
  }
  if (zone) {
    return { center: zone.center, zoom: zone.zoom, zone };
  }
  return { center: COLOMBIA_CENTER, zoom: COLOMBIA_ZOOM, zone: null };
}
